import { makeWaitForData } from 'components/shared/WaitForData';
import { ErrorMessages } from 'lib/AppErrors';
import AppEvents from 'lib/AppEvents';
import Config from 'lib/Config';
import { Goal } from 'lib/data/models/Goals';
import { ProviderNames } from 'lib/data/models/Providers';
import { PageHelper } from 'lib/helpers/PageHelper';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import EventService from 'lib/services/EventService';
import { getAccountProviders, getAccountTimezone, getGoal, getInnerError, renderError, renderLoader, renderNoDataMessage } from 'lib/utils/adaya/AdayaUtils';
import { isMobile, throttle } from 'lib/utils/UiUtils';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { combineAllDailyCampaignData, filterAdObjects, scoreCampaignData } from 'utils/adaya/ProviderUtils';
import { createIsoTimeRange } from 'utils/DateUtils';
import { getLogger } from 'utils/logging';
import CampaignsList from './components/CampaignsList';
import Header from './components/Header';
import './style.scss';
import { formatValue, getDataByMetric, getProbabilities, getProbabilityPercentages, getRoasRecommendationCode, last7Metrics } from './utils';

const { log, warn } = getLogger('Campaigns', { color: 'yellow', enabled: false });

const metricItems = [
    { value: 'Purchases', label: 'Purchases - Last 7 days' },
    { value: 'Revenue', label: 'Revenue - Last 7 days' },
    { value: 'AdSpend', label: 'AdSpend - Last 7 days' },
    { value: 'ROAS', label: 'ROAS - Last 7 days' },
    { value: 'CPP', label: 'CPP - Last 7 days' },
    { value: 'AOV', label: 'AOV - Last 7 days' }
];

const pageHelper = new PageHelper();

const Campaigns = (props) => {
    const [mobile, setMobile] = useState(isMobile());
    const [dataReady, setDataReady] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState('ROAS');
    const [rawData, setRawData] = useState(undefined);
    const [formattedData, setFormattedData] = useState(undefined);
    const [filteredRecCode, setFilteredRecCode] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);

    const handleResize = throttle(() => setMobile(isMobile()));
    const location = useLocation();

    const user = props.auth.user;
    const account = user.activeAccount;
    const timezone = getAccountTimezone(account);
    const providers = getAccountProviders(account, [ProviderNames.SHOPIFY]).map(p => p.name);
    const roasGoal = getGoal(account, Goal.Roas);

    // first load
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        EventService.subscribe([AppEvents.LOGIN, AppEvents.LOGOUT], clearData);
        return () => {
            window.removeEventListener('resize', handleResize);
            EventService.unsubscribe([AppEvents.LOGIN, AppEvents.LOGOUT], clearData);
        }
    }, []);

    useEffect(async () => {
        if (account) {
            await fetchData();
        }
    }, [account]);

    useEffect(() => {
        if (rawData) {
            let combinedData = combineAllDailyCampaignData(rawData);

            // filter "active" campaigns only.
            combinedData = filterAdObjects(combinedData, (ao, aoType) => ao.totals?.adspend > 0, false);
            scoreCampaignData(combinedData);

            // todo: move this to the scoring/processing functions globally?
            let data = combinedData.campaigns.map(c => {
                const [probDecRoas, probIncRoas] = getProbabilities(last7Metrics(rawData, c, 'ROAS'));
                const roasRecommendationCode = getRoasRecommendationCode(c, roasGoal, probDecRoas, probIncRoas);

                const totals = {
                    last7Orders: last7Metrics(rawData, c, 'Orders'),
                    last7Revenue: last7Metrics(rawData, c, 'Revenue'),
                    last7Adspend: last7Metrics(rawData, c, 'AdSpend'),
                    last7Roas: last7Metrics(rawData, c, 'ROAS'),
                    last7Cpp: last7Metrics(rawData, c, 'CPP'),
                    last7Aov: last7Metrics(rawData, c, 'AOV'),
                    probIncRoas,
                    probDecRoas,
                    totals: c.totals
                };

                return {
                    campaign: c,
                    data: getDataByMetric(selectedMetric, totals),
                    probability: getProbabilityPercentages(selectedMetric, totals),
                    roasRecommendationCode,
                    summaryValue: formatValue(selectedMetric, totals),
                }
            });

            const filtered = filteredRecCode === false ? data : data.filter(d => d.roasRecommendationCode === filteredRecCode);

            setFormattedData(filtered);
            setTimeout(() => setDataReady(true), 100); // grive graphjs a moment, ahem.
        }
    }, [rawData, selectedMetric, filteredRecCode]);

    useEffect(() => {
        if (location && location.state && location.state.recCode) {
            setFilteredRecCode(location.state.recCode);
        }
    }, [location]);

    useEffect(() => {
        if (!mobile) {
            if (filteredRecCode !== false) {
                setFilteredRecCode(false);
            }
        }
    }, [mobile]);

    const fetchData = async () => {
        if (!user) return;
        log(`fetchData`, user);
        setLoading(true);

        try {
            let r = await pageHelper.getDailyCampaignDataRequest(getFilterFromSelection());
            onDataResponse(r);
        } catch (e) {
            console.log(`Exception`, e)
            setError(getInnerError(e));
        }

        setLoading(false);
    }

    // Handles the response in any way it needs. This should not return anything.
    const onDataResponse = (r: ManagedRequest): void => {
        try {
            if (!r) throw "Error: ManagedRequest response was null";
            log('onDataResponse', r);

            if (r.status == RequestStatus.FINISHED) {
                // update UI data if the response matches the current request filter
                if (pageHelper.requestMatchesKey(r, getFilterFromSelection())) {
                    setFormattedData(undefined);
                    setTimeout(() => {
                        setRawData(r.response);
                    }, 0);
                } else {
                    log('Request response key does not match current filter, not showing data. Request:', r.key, ' Current:', currentFilterKey, currentFilter);
                }
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                let e = getInnerError(r);
                console.log(`Error`, e)
                setError(e);
            }
        } catch (e) {
            e = getInnerError(r);
            console.log(`Exception`, e)
            throw e;
        }
    }

    // Campaigns needs all campagn data except Shopify
    const getFilterFromSelection = (): CampaignDataFilter => {
        const timeRange = createIsoTimeRange('last7days');
        if (!account) throw `No account found for the current user.`;
        return { account, timeRange, providers };
    }

    const clearData = async () => {
        log(`clearData`);
        if (pageHelper.requestManager) pageHelper.requestManager.deleteAllRequests();
    }

    const hasData = () => {
        return formattedData != undefined;
    }

    return (
        <React.Fragment>
            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Campaigns</title></Helmet>
            <div id="campaigns-container" className={'page-layout ' + (dataReady ? 'ready' : '') + (error != undefined ? ' has-error' : '')}>

                {loading ? renderLoader() :

                    // error
                    error ? renderError(ErrorMessages.defaultErrorMessage(), error, "large light") :

                        // loaded but has no data
                        !hasData() ? renderNoDataMessage() :

                            // loaded and has data
                            <>
                                <Header
                                    mobile={mobile}
                                    selectedMetric={selectedMetric}
                                    setSelectedMetric={setSelectedMetric}
                                    metricItems={metricItems}
                                    filteredRecCode={filteredRecCode}
                                    setFilteredRecCode={setFilteredRecCode}
                                />
                                <CampaignsList
                                    data={formattedData}
                                    selectedMetric={selectedMetric}
                                    roasGoal={roasGoal}
                                    mobile={mobile}
                                    filteredRecCode={filteredRecCode}
                                />
                                <div className="footer"></div>
                            </>
                }
            </div>
        </React.Fragment>
    );

};

export default makeWaitForData(Campaigns, [], 'Campaigns');